import { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { getTagsTree } from '../lib/api';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Filter, X, Calendar, BookOpen } from 'lucide-react';

/**
 * FilterBar for analytics dashboards.
 * Reads/writes filters to URL search params.
 * Calls onFiltersChange(params) whenever filters change.
 */
export function AnalyticsFilterBar({ onFiltersChange }) {
  const [searchParams, setSearchParams] = useSearchParams();

  const [tagsTree, setTagsTree] = useState([]);
  const [dateFrom, setDateFrom] = useState(searchParams.get('date_from') || '');
  const [dateTo, setDateTo] = useState(searchParams.get('date_to') || '');
  const [subjectId, setSubjectId] = useState(searchParams.get('subject_id') || '');
  const [chapterId, setChapterId] = useState(searchParams.get('chapter_id') || '');

  useEffect(() => {
    getTagsTree().then(res => setTagsTree(res.data || [])).catch(() => {});
  }, []);

  // Derive subjects, chapters from tree
  const subjects = useMemo(() => tagsTree.filter(t => t.type === 'subject'), [tagsTree]);

  const chapters = useMemo(() => {
    if (!subjectId) return [];
    const subject = tagsTree.find(t => t.tag_id === subjectId);
    if (!subject) return [];
    const result = [];
    for (const theme of (subject.children || [])) {
      for (const chapter of (theme.children || [])) {
        result.push({ ...chapter, themeName: theme.label });
      }
    }
    return result;
  }, [subjectId, tagsTree]);

  const hasFilters = dateFrom || dateTo || subjectId || chapterId;

  const applyFilters = (overrides = {}) => {
    const f = {
      date_from: overrides.date_from ?? dateFrom,
      date_to: overrides.date_to ?? dateTo,
      subject_id: overrides.subject_id ?? subjectId,
      chapter_id: overrides.chapter_id ?? chapterId,
    };

    // Update URL params
    const params = new URLSearchParams();
    Object.entries(f).forEach(([k, v]) => { if (v) params.set(k, v); });
    setSearchParams(params, { replace: true });

    // Clean empty values
    const clean = {};
    Object.entries(f).forEach(([k, v]) => { if (v) clean[k] = v; });
    onFiltersChange(clean);
  };

  const resetFilters = () => {
    setDateFrom(''); setDateTo(''); setSubjectId(''); setChapterId('');
    setSearchParams({}, { replace: true });
    onFiltersChange({});
  };

  // Trigger on subject change → clear chapter
  const handleSubjectChange = (v) => {
    const val = v === '_all' ? '' : v;
    setSubjectId(val);
    setChapterId('');
    applyFilters({ subject_id: val, chapter_id: '' });
  };

  const handleChapterChange = (v) => {
    const val = v === '_all' ? '' : v;
    setChapterId(val);
    applyFilters({ chapter_id: val });
  };

  const handleDateApply = () => applyFilters();

  return (
    <div className="flex flex-wrap items-end gap-3 p-3 rounded-lg bg-muted/20 border border-border" data-testid="analytics-filter-bar">
      <div className="flex items-center gap-1.5 text-sm text-muted-foreground mr-1">
        <Filter className="w-4 h-4" /> Filtres
      </div>

      {/* Date range */}
      <div className="flex items-center gap-2">
        <div className="flex items-center gap-1">
          <Calendar className="w-3.5 h-3.5 text-muted-foreground" />
          <Input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className="h-8 w-36 text-xs" data-testid="filter-date-from" placeholder="Du" />
        </div>
        <span className="text-muted-foreground text-xs">→</span>
        <Input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} className="h-8 w-36 text-xs" data-testid="filter-date-to" placeholder="Au" />
        {(dateFrom || dateTo) && (
          <Button variant="secondary" size="sm" className="h-8 text-xs" onClick={handleDateApply} data-testid="filter-date-apply">Appliquer</Button>
        )}
      </div>

      {/* Subject */}
      {subjects.length > 0 && (
        <div className="flex items-center gap-1">
          <BookOpen className="w-3.5 h-3.5 text-muted-foreground" />
          <Select value={subjectId || '_all'} onValueChange={handleSubjectChange}>
            <SelectTrigger className="h-8 w-40 text-xs" data-testid="filter-subject">
              <SelectValue placeholder="Matière" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="_all"><span className="text-muted-foreground">Toutes matières</span></SelectItem>
              {subjects.map(s => <SelectItem key={s.tag_id} value={s.tag_id}>{s.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      )}

      {/* Chapter (cascaded from subject) */}
      {chapters.length > 0 && (
        <Select value={chapterId || '_all'} onValueChange={handleChapterChange}>
          <SelectTrigger className="h-8 w-44 text-xs" data-testid="filter-chapter">
            <SelectValue placeholder="Section" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="_all"><span className="text-muted-foreground">Toutes sections</span></SelectItem>
            {chapters.map(c => <SelectItem key={c.tag_id} value={c.tag_id}>{c.label}</SelectItem>)}
          </SelectContent>
        </Select>
      )}

      {/* Reset */}
      {hasFilters && (
        <Button variant="ghost" size="sm" className="h-8 text-xs text-muted-foreground hover:text-foreground" onClick={resetFilters} data-testid="filter-reset">
          <X className="w-3.5 h-3.5 mr-1" /> Réinitialiser
        </Button>
      )}
    </div>
  );
}
